import { TEST_ID_ADD_PLAYERS_PANEL } from "e2e/testId";
import React, { memo } from "react";

import { TONE_CLASS } from "@/components/atoms/Badge";
import { Box } from "@/components/atoms/Box";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import { SuggestionReasonCategory } from "@/generated/graphql";

import type { SuggestionReasonCopy } from "../../../constants/suggestionReasons";

/** A reason resolved to its display copy. */
export type ResolvedReason = SuggestionReasonCopy & {
  readonly category: SuggestionReasonCategory;
};

/** Stable, per-chip handlers built once by the container. */
export interface ReasonChipHandlers {
  /**
   * PIN the explanation — works on every platform. Pressing the chip whose
   * explanation is already pinned dismisses it (SE-6265).
   */
  readonly onPress: () => void;
  /** Reveal on hover, transiently — fires on web only. */
  readonly onHoverIn: () => void;
}

/** Props for {@link SuggestionReasonChipsView}. */
interface SuggestionReasonChipsViewProps {
  /** Reasons that have copy. An empty list renders nothing at all. */
  readonly reasons: readonly ResolvedReason[];
  /** The reason whose full explanation is currently revealed, if any. */
  readonly activeReason: ResolvedReason | null;
  /** Per-category handlers, keyed by category. */
  readonly handlers: Partial<
    Record<SuggestionReasonCategory, ReasonChipHandlers>
  >;
  /** Hide the explanation when the pointer leaves. */
  readonly onHoverOut: () => void;
}

/**
 * Compact category chips for a suggested candidate, with the full explanation
 * revealed beneath them.
 *
 * Hover is a web-only affordance, so it is an ENHANCEMENT here, not the
 * mechanism: the same explanation is reachable by pressing a chip on any
 * platform, and every chip carries its full explanation as its accessibility
 * label so assistive tech never has to hover or press at all. The chip itself
 * is not platform-gated — only the hover handlers are, and those simply never
 * fire on native.
 *
 * Which reason is revealed — and how press and hover interact once both are in
 * play on the same surface — is the container's business; see its docblock for
 * the pinned-vs-hovered contract.
 * @param root0 - Component props.
 * @param root0.reasons - Reasons that have copy.
 * @param root0.activeReason - Reason whose explanation is revealed.
 * @param root0.handlers - Per-category handlers.
 * @param root0.onHoverOut - Hide the transient hover reveal (web).
 */
const SuggestionReasonChipsView = ({
  reasons,
  activeReason,
  handlers,
  onHoverOut,
}: SuggestionReasonChipsViewProps) =>
  reasons.length === 0 ? null : (
    <Stack className="gap-1">
      <Stack direction="horizontal" className="flex-wrap gap-1">
        {reasons.map(reason => (
          <Pressable
            key={reason.category}
            testID={TEST_ID_ADD_PLAYERS_PANEL.REASON_CHIP(reason.category)}
            accessibilityRole="button"
            accessibilityLabel={reason.explanation}
            onPress={handlers[reason.category]?.onPress}
            onHoverIn={handlers[reason.category]?.onHoverIn}
            onHoverOut={onHoverOut}
          >
            {/* The PC frames draw reason chips in the info pairing (the
                Badge atom's info-soft family), not the old neutral gray.
                rounded-sm rather than a hand-rolled pill radius: the
                design:budgets ratchet caps feature Views that roll their own
                circular styling. */}
            <Box
              className={`rounded-sm px-2 py-px ${TONE_CLASS["info-soft"].box}`}
            >
              <Text variant="micro" className={TONE_CLASS["info-soft"].text}>
                {reason.label}
              </Text>
            </Box>
          </Pressable>
        ))}
      </Stack>

      {activeReason === null ? null : (
        <Text
          testID={TEST_ID_ADD_PLAYERS_PANEL.REASON_EXPLANATION}
          variant="micro"
          className="text-content-muted"
        >
          {activeReason.explanation}
        </Text>
      )}
    </Stack>
  );

SuggestionReasonChipsView.displayName = "SuggestionReasonChipsView";

export default memo(SuggestionReasonChipsView);
